import { useMemo, useCallback } from 'react';
import { useUnitConversion } from './useUnitConversion';
import { UnitsEnum } from '@/__generated__/graphql';
import { CustomBlockDefects } from '@/types';

// Generic calculation data interface
type CalculationData = {
  totalItems: number;
  selectedItems: number;
  primaryValue: number;
  totalValue: number;
  percentage: number;
  hasData: boolean;
};

// Generic calculation options
type CalculationOptions = {
  data?: CustomBlockDefects;
  selectedItems: string[];
  getItemName: (item: CustomBlockDefects['defectsByPaintBlock'][0]) => string;
  getPrimaryValue: (item: CustomBlockDefects['defectsByPaintBlock'][0]) => number;
  getTotalValue: (item: CustomBlockDefects['defectsByPaintBlock'][0]) => number;
  getTotalCount: (data: CustomBlockDefects) => number;
  getItems: (data: CustomBlockDefects) => CustomBlockDefects['defectsByPaintBlock'];
  unit?: UnitsEnum;
};

// Generic calculation hook
export const useCalculationData = ({
  data,
  selectedItems,
  getItemName,
  getPrimaryValue,
  getTotalValue,
  getTotalCount,
  getItems,
  unit,
}: CalculationOptions): CalculationData => {
  const { getUnitSuffix } = useUnitConversion();

  const convertValue = useCallback(
    (value: number): number => {
      if (!unit) return value;
      const unitSuffix = getUnitSuffix({ unit });
      if (unit === UnitsEnum.SquareMeter) {
        if (unitSuffix === 'ft²') {
          return value * 10.7639;
        }
        return value;
      }
      return value;
    },
    [unit, getUnitSuffix]
  );
  return useMemo(() => {
    const hasValidData = data && Object.keys(data).length > 0;

    if (!hasValidData) {
      // eslint-disable-next-line no-console
      console.log('useCalculationData: No valid data provided', { data });
      return {
        totalItems: 0,
        selectedItems: selectedItems.length,
        primaryValue: 0,
        totalValue: 0,
        percentage: 0,
        hasData: false,
      };
    }

    try {
      const allItems = getItems(data);
      const selectedItemNames = new Set(selectedItems);

      const selectedItemsData = allItems.filter((item) => {
        const itemName = getItemName(item);
        const isSelected = selectedItemNames.has(itemName);
        return isSelected;
      });

      const { primaryValue, totalValue } = selectedItemsData.reduce<{
        primaryValue: number;
        totalValue: number;
      }>(
        (acc, item) => {
          const itemPrimaryValue = getPrimaryValue(item) || 0;
          const itemTotalValue = getTotalValue(item) || 0;

          return {
            primaryValue: acc.primaryValue + itemPrimaryValue,
            totalValue: acc.totalValue + itemTotalValue,
          };
        },
        { primaryValue: 0, totalValue: 0 }
      );

      const convertedPrimaryValue = convertValue(primaryValue);
      const convertedTotalValue = convertValue(totalValue);

      const percentage =
        convertedTotalValue > 0
          ? Math.round((convertedPrimaryValue / convertedTotalValue) * 100)
          : 0;

      const totalCount = typeof getTotalCount === 'function' ? getTotalCount(data) : 0;

      const roundToTwo = (num: number): number => Math.round((num + Number.EPSILON) * 100) / 100;

      const result = {
        totalItems: totalCount,
        selectedItems: selectedItemsData.length,
        primaryValue: roundToTwo(convertedPrimaryValue),
        totalValue: roundToTwo(convertedTotalValue),
        percentage,
        hasData: true,
      };

      return result;
    } catch (error) {
      console.error('Error in useCalculationData:', error);
      return {
        totalItems: 0,
        selectedItems: 0,
        primaryValue: 0,
        totalValue: 0,
        percentage: 0,
        hasData: false,
      };
    }
  }, [
    data,
    selectedItems,
    getItemName,
    getPrimaryValue,
    getTotalValue,
    getTotalCount,
    getItems,
    convertValue,
  ]);
};
